import { ref } from 'vue'
import axios from 'axios'
import { sendGraphQLQuery } from '@/services/gql-util'
import { Collection, CollectionItem } from '@/types/Collections'

export function useCollections() {
	const collections = ref<Collection[]>([])
	const loading = ref(false)
	const error = ref<string | null>(null)

	const getCollections = async (vhost: string): Promise<void> => {
		loading.value = true
		error.value = null

		const query = `
		query ($vhost: Int!) {
			Collections {
				collectionList(vhost: $vhost) {
					id
					name
					description
					vhost
					type
					mediaCount
					thumbUrl
					createdBy
					createdDate
					updatedDate
				}
			}
		}
		`

		try {
			const data = await sendGraphQLQuery(query, { vhost: +vhost })
			if (data?.Collections?.collectionList) {
				collections.value = [...data.Collections.collectionList]
			}
		} catch (err: any) {
			console.error('Error fetching collections:', err)
			error.value = err.message || 'Failed to fetch collections'
			// Fallback to REST
			try {
				const response = await axios.post('/services/json', {
					method: 'collections.getCollections',
					vhostID: vhost,
				})
				if (response.data.status) {
					collections.value = response.data.result.data || []
				}
			} catch (restErr) {
				console.error('REST fallback also failed:', restErr)
			}
		} finally {
			loading.value = false
		}
	}

	const getCollectionItems = async (collectionId: number): Promise<CollectionItem[]> => {
		const query = `
		query ($collectionId: Int!) {
			Collections {
				collectionItems(collectionId: $collectionId) {
					id
					mediaId
					title
					thumbUrl
					filetype
					sortOrder
				}
			}
		}
		`

		try {
			const data = await sendGraphQLQuery(query, { collectionId })
			return data?.Collections?.collectionItems || []
		} catch (err) {
			console.error('Error fetching collection items:', err)
			return []
		}
	}

	const createCollection = async (vhost: string, name: string, description: string, type: string): Promise<number | null> => {
		const mutation = `
		mutation ($vhost: Int!, $name: String!, $description: String, $type: String!) {
			CollectionMutations {
				createCollection(vhost: $vhost, name: $name, description: $description, type: $type)
			}
		}
		`

		try {
			const data = await sendGraphQLQuery(mutation, {
				vhost: +vhost,
				name,
				description,
				type,
			})
			return data?.CollectionMutations?.createCollection || null
		} catch (err) {
			console.error('Error creating collection:', err)
			// Fallback to REST
			try {
				const response = await axios.post('/services/json', {
					method: 'collections.createCollection',
					vhostID: vhost,
					name,
					description,
					type,
				})
				return response.data.result || null
			} catch (restErr) {
				console.error('REST fallback also failed:', restErr)
				return null
			}
		}
	}

	const deleteCollection = async (collectionId: number): Promise<boolean> => {
		const mutation = `
		mutation ($collectionId: Int!) {
			CollectionMutations {
				deleteCollection(collectionId: $collectionId)
			}
		}
		`

		try {
			await sendGraphQLQuery(mutation, { collectionId })
			return true
		} catch (err) {
			console.error('Error deleting collection:', err)
			return false
		}
	}

	const addMediaToCollection = async (collectionId: number, mediaId: number): Promise<boolean> => {
		const mutation = `
		mutation ($collectionId: Int!, $mediaId: Int!) {
			CollectionMutations {
				addMediaToCollection(collectionId: $collectionId, mediaId: $mediaId)
			}
		}
		`

		try {
			await sendGraphQLQuery(mutation, { collectionId, mediaId })
			return true
		} catch (err) {
			console.error('Error adding media to collection:', err)
			return false
		}
	}

	const removeMediaFromCollection = async (collectionId: number, mediaId: number): Promise<boolean> => {
		const mutation = `
		mutation ($collectionId: Int!, $mediaId: Int!) {
			CollectionMutations {
				removeMediaFromCollection(collectionId: $collectionId, mediaId: $mediaId)
			}
		}
		`

		try {
			await sendGraphQLQuery(mutation, { collectionId, mediaId })
			return true
		} catch (err) {
			console.error('Error removing media from collection:', err)
			return false
		}
	}

	return {
		collections,
		loading,
		error,
		getCollections,
		getCollectionItems,
		createCollection,
		deleteCollection,
		addMediaToCollection,
		removeMediaFromCollection,
	}
}
